import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import get from 'lodash.get';

// components
import {
    deliveryReturnsTitle,
    deliveryTitle,
    returnPolicyTitle,
    paymentsTitle,
    invoiceTitle,
} from '../messages/pdpDetailsMessages';
import { PdpSharedDetailShipping } from '../PdpSharedDetails/PdpSharedDetailShipping';
import { PdpSharedDetailReturnPolicy } from '../PdpSharedDetails/PdpSharedDetailReturnPolicy';
import { PdpSharedDetailPayments } from '../PdpSharedDetails/PdpSharedDetailPayments';
import { PdpSharedDetailItemInvoice } from '../PdpSharedDetails/PdpSharedDetailItemInvoice';
import { PdpSharedDepositPaymentDetail } from '../PdpSharedDetails/PdpSharedDepositPaymentDetail';
import { PdpRespDetail } from './PdpRespDetail';

import ShippingIcon from 'dibs-icons/exports/Shipping';
import ReturnArrow from 'dibs-icons/exports/ReturnArrow';

// styles
import styles from './PdpRespDetails.scss';

const columnStyles = `colLg6 colXs12 ${styles.columnShippingReturns}`;

function PdpRespShippingReturnsComponent({
    isClient,
    currency,
    relayVariables,
    viewer,
    item,
    user,
    shipmentQuote,
    shipment,
    skuId,
    isPdpFreeShippingTestVariant,
}) {
    const isAvailable = get(item, 'isAvailable');
    const isSold = get(item, 'isSold');
    const hasPrivateListing = get(item, 'hasPrivateListing');
    const hasActiveHoldForBuyer = get(item, 'hasActiveHoldForBuyer');
    const showInvoice =
        viewer.isItemInvoiceEnabled &&
        (isAvailable || hasActiveHoldForBuyer) &&
        !isSold &&
        !hasPrivateListing;

    return (
        <div className={styles.wrapper} data-tn="pdp-details-shipping">
            <h2 className={styles.header}>{deliveryReturnsTitle}</h2>
            <div className="rowFlex">
                <ul className={columnStyles}>
                    {/* Shipping */}
                    <PdpRespDetail
                        title={
                            isPdpFreeShippingTestVariant ? (
                                <div className={styles.titleWithIconContainer}>
                                    <ShippingIcon className={styles.shippingIcon} />
                                    {deliveryTitle}
                                </div>
                            ) : (
                                deliveryTitle
                            )
                        }
                        dataTn="shipping"
                        printHide
                    >
                        <PdpSharedDetailShipping
                            currency={currency}
                            userId={relayVariables.userId}
                            itemId={relayVariables.itemId}
                            item={item}
                            viewer={viewer}
                            user={user}
                            shipmentQuote={shipmentQuote}
                            shipment={shipment}
                            isPdpFreeShippingTestVariant={isPdpFreeShippingTestVariant}
                        />
                    </PdpRespDetail>
                    {/* Return Policy */}
                    <PdpRespDetail
                        title={
                            isPdpFreeShippingTestVariant ? (
                                <div className={styles.titleWithIconContainer}>
                                    <ReturnArrow className={styles.returnIcon} />
                                    {returnPolicyTitle}
                                </div>
                            ) : (
                                returnPolicyTitle
                            )
                        }
                        dataTn="return-policy"
                    >
                        <PdpSharedDetailReturnPolicy
                            userId={relayVariables.userId}
                            itemId={relayVariables.itemId}
                            item={item}
                        />
                    </PdpRespDetail>
                </ul>
                <ul className={columnStyles}>
                    {/* Online Payment Methods */}
                    <PdpRespDetail title={paymentsTitle} dataTn="payment-methods">
                        <PdpSharedDetailPayments
                            isClient={isClient}
                            currency={currency}
                            user={user}
                            item={item}
                        />
                    </PdpRespDetail>
                    {/* Item Invoice */}
                    <PdpSharedDepositPaymentDetail
                        userId={relayVariables.userId}
                        item={item}
                        wrapper={PdpRespDetail}
                    />
                    {showInvoice && (
                        <PdpRespDetail title={invoiceTitle} dataTn="item-invoice" printHide>
                            <PdpSharedDetailItemInvoice
                                itemId={relayVariables.itemId}
                                skuId={skuId}
                                user={user}
                            />
                        </PdpRespDetail>
                    )}
                </ul>
            </div>
        </div>
    );
}

PdpRespShippingReturnsComponent.propTypes = {
    currency: PropTypes.string,
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        itemId: PropTypes.string.isRequired,
    }).isRequired,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    shipmentQuote: PropTypes.object,
    shipment: PropTypes.object,
    skuId: PropTypes.string,
    isClient: PropTypes.bool,
    isPdpFreeShippingTestVariant: PropTypes.bool,
};

export const PdpRespShippingReturns = createFragmentContainer(PdpRespShippingReturnsComponent, {
    viewer: graphql`
        fragment PdpRespShippingReturns_viewer on Viewer {
            ...PdpSharedDetailShipping_viewer
            isItemInvoiceEnabled: isEnabledByLocale(featureName: "INVOICE")
        }
    `,
    user: graphql`
        fragment PdpRespShippingReturns_user on User {
            ...PdpSharedDetailPayments_user
            ...PdpSharedDetailShipping_user
            ...PdpSharedDetailItemInvoice_user
        }
    `,
    item: graphql`
        fragment PdpRespShippingReturns_item on Item {
            ...PdpSharedDetailPayments_item
            ...PdpSharedDetailReturnPolicy_item
            ...PdpSharedDepositPaymentDetail_item
            ...PdpSharedDetailShipping_item
            isAvailable
            isSold
            hasPrivateListing
            hasActiveHoldForBuyer(buyerId: $userId) @include(if: $fetchUser)
        }
    `,
    shipmentQuote: graphql`
        fragment PdpRespShippingReturns_shipmentQuote on ShipmentQuote {
            ...PdpSharedDetailShipping_shipmentQuote
        }
    `,
    shipment: graphql`
        fragment PdpRespShippingReturns_shipment on Shipment {
            ...PdpSharedDetailShipping_shipment
        }
    `,
});
